import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HiOutlineTrendingUp } from "react-icons/hi";
import { apiGetRequest } from "helpers/Requests";
import { URL_SP_AI_USAGE_COST_SERIES } from "store/services/URL";
import {
  buildQuery,
  CHART_FIXED_HEIGHT,
  CHART_WIDGET_HEIGHT,
  fillCostSeriesRange,
  formatAudSmart,
  formatCount,
  formatPeriodLabel,
  getChartDateRange,
  getChartPlotMargin,
  getChartYAxisProps,
  getCostAxisScale,
  getCostChartTickInterval,
} from "./aiUsageChartUtils";
import ChartCardHeader from "./ChartCardHeader";
import { ChartMetricStrip, ChartWidgetState } from "./ChartWidgetBody";
import { useChartAxisLayout } from "./useChartAxisLayout";

const CostTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="min-w-[180px] rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-xs shadow-lg">
      <p className="font-semibold text-gray-900">{row.label}</p>
      <div className="mt-2 space-y-1.5 text-gray-600">
        <p>
          Credit spend:{" "}
          <span className="font-medium text-[#4B6CB7]">
            {formatAudSmart(row.cost_aud)}
          </span>
        </p>
        <p>
          AI calls:{" "}
          <span className="font-medium text-gray-900">
            {formatCount(row.call_count)}
          </span>
        </p>
        {row.shareOfTotal ? (
          <p>
            Share of period:{" "}
            <span className="font-medium text-gray-700">{row.shareOfTotal}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
};

const AiCostOverTimeChart = () => {
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [series, setSeries] = useState([]);
  const [granularity, setGranularity] = useState("day");

  const range = useMemo(() => getChartDateRange(period), [period]);
  const isMonthlyView = granularity === "month";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    apiGetRequest(
      `${URL_SP_AI_USAGE_COST_SERIES}${buildQuery({
        from: range.from,
        to: range.to,
        granularity: range.granularity,
      })}`
    )
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? {};
        setGranularity(payload?.granularity || range.granularity);
        setSeries(Array.isArray(payload?.series) ? payload.series : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSeries([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load credit consumption."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, range.granularity]);

  const chartData = useMemo(() => {
    const filled = fillCostSeriesRange(
      series,
      range.from,
      range.to,
      granularity
    );
    const rows = filled.map((row) => ({
      ...row,
      cost_aud: Number(row.cost_aud) || 0,
      call_count: Number(row.call_count) || 0,
      label: formatPeriodLabel(row.period, granularity, {
        shortMonth: granularity === "month",
      }),
    }));
    const totalSpend = rows.reduce((sum, row) => sum + row.cost_aud, 0);
    return rows.map((row) => ({
      ...row,
      shareOfTotal:
        totalSpend > 0
          ? `${((row.cost_aud / totalSpend) * 100).toFixed(1)}%`
          : "0%",
    }));
  }, [series, granularity, range.from, range.to]);

  const hasUsage = useMemo(
    () => chartData.some((row) => row.cost_aud > 0 || row.call_count > 0),
    [chartData]
  );

  const xAxisTickInterval = useMemo(
    () => getCostChartTickInterval(chartData.length, granularity),
    [chartData.length, granularity]
  );

  const { containerRef, compact } = useChartAxisLayout();
  const yAxisScale = useMemo(
    () => getCostAxisScale(chartData.map((row) => row.cost_aud), compact),
    [chartData, compact]
  );

  const summary = useMemo(() => {
    const totalSpend = chartData.reduce((sum, row) => sum + row.cost_aud, 0);
    const totalCalls = chartData.reduce((sum, row) => sum + row.call_count, 0);
    const peak = chartData.reduce(
      (best, row) => (row.cost_aud > (best?.cost_aud || 0) ? row : best),
      null
    );
    return { totalSpend, totalCalls, peak };
  }, [chartData]);

  const periodSubtitle =
    granularity === "month"
      ? "Monthly AI credit spend for the selected period"
      : "Daily AI credit spend for the selected period";

  const showDots = chartData.length <= 14;

  return (
    <div
      className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      style={{ height: CHART_WIDGET_HEIGHT }}
    >
      <ChartCardHeader
        icon={HiOutlineTrendingUp}
        title="Credit consumption"
        subtitle={periodSubtitle}
        period={period}
        onPeriodChange={setPeriod}
        dateFrom={range.from}
        dateTo={range.to}
      />

      {!loading && !error && chartData.length > 0 ? (
        <ChartMetricStrip
          items={[
            { label: "Total spend", value: formatAudSmart(summary.totalSpend) },
            { label: "Total calls", value: formatCount(summary.totalCalls) },
            {
              label: "Peak period",
              value: hasUsage ? summary.peak?.label || "—" : "—",
            },
          ]}
        />
      ) : null}

      <div
        ref={containerRef}
        className="flex min-w-0 flex-1 flex-col px-4 pt-2 pb-4 sm:px-5"
      >
        <ChartWidgetState
          loading={loading}
          error={error}
          empty={!loading && !error && chartData.length === 0}
          emptyTitle="No credit usage in this period"
          emptyHint="Usage will appear here after AI features run."
          skeletonVariant="area"
        >
          <div className="min-w-0 w-full" style={{ height: CHART_FIXED_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={getChartPlotMargin({
                  angledXAxis: isMonthlyView,
                  extraRight: isMonthlyView ? 8 : 16,
                })}
              >
                <defs>
                  <linearGradient id="aiCostFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4B6CB7" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#4B6CB7" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f4f6"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: isMonthlyView || compact ? 9 : 10,
                    fill: "#9ca3af",
                  }}
                  axisLine={false}
                  tickLine={false}
                  interval={xAxisTickInterval}
                  minTickGap={isMonthlyView ? 0 : 12}
                  angle={isMonthlyView ? -42 : 0}
                  textAnchor={isMonthlyView ? "end" : "middle"}
                  height={isMonthlyView ? 56 : 28}
                  dy={isMonthlyView ? 2 : 4}
                />
                <YAxis
                  {...getChartYAxisProps(yAxisScale, { compact })}
                />
                <Tooltip content={<CostTooltip />} />
                <Area
                  type="monotone"
                  dataKey="cost_aud"
                  stroke="#4B6CB7"
                  strokeWidth={2}
                  fill="url(#aiCostFill)"
                  dot={
                    showDots
                      ? { r: 2.5, fill: "#4B6CB7", strokeWidth: 0 }
                      : false
                  }
                  activeDot={{
                    r: 4,
                    fill: "#293F6A",
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartWidgetState>
      </div>
    </div>
  );
};

export default AiCostOverTimeChart;
